import * as React from 'react';

import throttle from 'lodash/throttle';

import { IElementScrollerProps, IScrollConfigData } from './element-scroller.props.autogenerated';

/**
 *
 * ScrollAnimContainer component
 * @extends {React.PureComponent<IScrollAnimContainerProps<IScrollAnimContainerData>>}
 */

class ElementScroller extends React.PureComponent<IElementScrollerProps<{}>> {

    /**
     * used to stop events from taking too much processing
     */
    private _throttledScrollHandler: () => void;

    /**
     * used to cache DOM elements to improve performance
     */
    private _cachedDOMQueries: Map<string, HTMLElement>;

    public constructor(props: IElementScrollerProps<{}>) {
        super(props);

        this._throttledScrollHandler = throttle(this._scrollHandler.bind(this), 25);
        this._isAnyPartOfElementInViewport = this._isAnyPartOfElementInViewport.bind(this);
        this._cachedDOMQueries = new Map();
    }

    public componentDidMount(): void {
        if (process.env.CURRENT_ENVIRONMENT === 'web') {

            window && window.addEventListener &&
                window.addEventListener('scroll', this._throttledScrollHandler);

            this._scrollHandler();
        }
    }

    public render(): JSX.Element {
        return (<div />);
    }

    private _scrollHandler(): void {
        // use this method for possible performance benefits
        // const isInViewport = this._isAnyPartOfElementInViewport(this._translateTarget);
        this._scrollConfig.forEach(scrollConfig => {
            const target = this._selectElement(scrollConfig.targetElement);
            if (target) {
                const translateX = this._calculateTranslateX(scrollConfig);
                target.style.transform = `translate(${translateX}%, 0)`;
            }
        });
    }

    /**
     * function by https://gist.github.com/StokeMasterJack
     */
    private _isAnyPartOfElementInViewport(el?: Element | null): boolean {
        if (!el) { return false; }

        const rect = el.getBoundingClientRect();
        // DOMRect { x: 8, y: 8, width: 100, height: 100, top: 8, right: 108, bottom: 108, left: 8 }
        const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
        const windowWidth = (window.innerWidth || document.documentElement.clientWidth);

        // http://stackoverflow.com/questions/325933/determine-whether-two-date-ranges-overlap
        const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
        const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

        return (vertInView && horInView);
    }

    private _calculateTranslateX(scrollConfig: IScrollConfigData): number | null {
        const trigger = this._selectElement(scrollConfig.triggerElement);
        const endingTrigger = this._selectElement(scrollConfig.endTriggerElement);

        // if the triggers don't exist, we can't calculate X
        if (!trigger || !endingTrigger) { return null; }

        const animationAdjustments = this._getScrollingAdjustments(scrollConfig);

        const triggerPos = trigger.offsetTop;
        const endingTriggerPos = endingTrigger.offsetTop;
        const clientPos = window.scrollY + window.innerHeight + animationAdjustments.offset;

        // converts the relative position of the clients scroll from the trigger locations to a percent value from [0,1]
        const animationPercentCompletion = (clientPos - triggerPos) / (endingTriggerPos - triggerPos);

        // transformation from a percent value to another percent value in a given range from the config
        let translateValue = (animationPercentCompletion * scrollConfig.endTranslationValue - animationPercentCompletion * scrollConfig.startTranslationValue) + scrollConfig.startTranslationValue;

        // rate adjuster
        translateValue *= animationAdjustments.rate;
        this.props.context.telemetry.debug(`[animationPercentCompletion] ${animationPercentCompletion}`);
        this.props.context.telemetry.debug(`[finalTranslateValue] ${translateValue}`);
        return translateValue;
    }

    private _selectElement(selector: string): HTMLElement | null {
        if (this._cachedDOMQueries.has(selector)) {
            return this._cachedDOMQueries.get(selector)!;
        }
        const $el = document.querySelector<HTMLElement>(selector);
        if (!$el) { return null; }
        this._cachedDOMQueries.set(selector, $el);
        return $el;
    }

    private _getScrollingAdjustments(config: IScrollConfigData): { rate: number; offset: number } {
        const { gridSettings } = this.props.context.request;
        const width = window.innerWidth;
        if (gridSettings) {
            const { lg, md } = gridSettings;
            if (md && width < md.w) { return { rate: config.mobileAdjustments.rate, offset: config.mobileAdjustments.offset }; }
            if (lg && width < lg.w) { return { rate: config.tabletAdjustments.rate, offset: config.tabletAdjustments.offset }; }
            // anything larger is a desktop
            return { rate: config.desktopAdjustments.rate, offset: config.desktopAdjustments.offset };
        }
        // if none were set somehow, default to 1, 0
        return {
            rate: 1,
            offset: 0
        };
    }

    private get _scrollConfig(): IScrollConfigData[] {
        return this.props.config.scrollConfig || [];

        // use this for testing
        // return [
        //     {
        //         triggerElement: `.ms-content-block__title`,
        //         endTriggerElement: '.featured-heading-outline .ms-content-block__title',
        //         targetElement: `.community-image-container`,
        //         endTranslationValue: -100,
        //         startTranslationValue: 100,
        //         translationRate: 1,
        //         startTriggerOffset: 0
        //     }
        // ];
    }
}

export default ElementScroller;
